import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "./contexts/CartContext";
import Layout from "./components/Layout";
// Home is the landing/LCP page — kept eager so it paints without a chunk round-trip.
import Home from "./pages/Home";
// Every other route is code-split: its JS only downloads when the user visits it,
// so the initial homepage payload no longer carries the whole site.
const Products = lazy(() => import("./pages/Products"));
const BracketsHub = lazy(() => import("./pages/BracketsHub"));
const Brackets = lazy(() => import("./pages/Brackets"));
const FlatBracket = lazy(() => import("./pages/FlatBracket"));
const InstallationKits = lazy(() => import("./pages/InstallationKits"));
const BulkPacks = lazy(() => import("./pages/BulkPacks"));
const FencePosts = lazy(() => import("./pages/FencePosts"));
const PostProduct = lazy(() => import("./pages/PostProduct"));
const AnimalHurdle = lazy(() => import("./pages/AnimalHurdle"));
const FenceExtensions = lazy(() => import("./pages/FenceExtensions"));
const FenceExtension = lazy(() => import("./pages/FenceExtension"));
const AnimalExtensions = lazy(() => import("./pages/AnimalExtensions"));
const AnimalExtension = lazy(() => import("./pages/AnimalExtension"));
const ConcretePicketBase = lazy(() => import("./pages/ConcretePicketBase"));
const PreDrilledAngle = lazy(() => import("./pages/PreDrilledAngle"));
const Applications = lazy(() => import("./pages/Applications"));
const Application = lazy(() => import("./pages/Application"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Review = lazy(() => import("./pages/Review"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PackPage = lazy(() => import("./pages/PackPage"));
const ReturnsWarranty = lazy(() => import("./pages/ReturnsWarranty"));
const Trade = lazy(() => import("./pages/Trade"));
const Faq = lazy(() => import("./pages/Faq"));
const InternationalShipping = lazy(() => import("./pages/InternationalShipping"));
const InternationalQuote = lazy(() => import("./pages/InternationalQuote"));
const NotFound = lazy(() => import("./pages/NotFound"));
import "./App.css";

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              {/* Brackets hub (category) + each bracket's own product page. The
                  hub took over /products/brackets; the supported bracket's
                  configurator moved to its own URL below. */}
              <Route path="/products/brackets" element={<BracketsHub />} />
              <Route path="/products/supported-bracket" element={<Brackets />} />
              <Route path="/products/flat-bracket" element={<FlatBracket />} />
              <Route path="/products/installation-kits" element={<InstallationKits />} />
              <Route path="/products/bulk-packs" element={<BulkPacks />} />
              {/* Fence Posts — two distinct products on a dedicated hub */}
              <Route path="/posts" element={<FencePosts />} />
              <Route path="/posts/notched-groove" element={<PostProduct slug="notched-groove" />} />
              <Route path="/posts/tube-post" element={<PostProduct slug="tube-post" />} />
              {/* Old picket URL now resolves to the Fence Posts hub */}
              <Route path="/products/star-pickets" element={<Navigate to="/posts" replace />} />
              <Route path="/products/animal-hurdle" element={<AnimalHurdle />} />
              {/* Fence Height Extensions — collection + two separate products */}
              <Route path="/products/fence-height-extensions" element={<FenceExtensions />} />
              <Route path="/products/galvanised-fence-height-extension" element={<FenceExtension materialKey="galvanised" />} />
              <Route path="/products/aluminium-fence-height-extension" element={<FenceExtension materialKey="aluminium" />} />
              {/* Animal Extensions — collection + new star-picket product (tube version stays at /products/animal-hurdle) */}
              <Route path="/products/animal-extensions" element={<AnimalExtensions />} />
              <Route path="/products/star-picket-animal-extension" element={<AnimalExtension />} />
              {/* Anchor Base Bracket — the bracket that mounts a star picket onto concrete */}
              <Route path="/products/anchor-bracket" element={<ConcretePicketBase />} />
              {/* Components & Accessories — supplied steel angle */}
              <Route path="/products/pre-drilled-galvanised-angle" element={<PreDrilledAngle />} />
              {/* Applications / Build Ideas — scalable "what you can build" hub */}
              <Route path="/applications" element={<Applications />} />
              <Route path="/applications/:slug" element={<Application />} />
              <Route path="/products/single-bracket" element={<PackPage slug="single-bracket" />} />
              <Route path="/products/10-pack" element={<PackPage slug="10-pack" />} />
              <Route path="/products/20-pack" element={<PackPage slug="20-pack" />} />
              <Route path="/products/30-pack" element={<PackPage slug="30-pack" />} />
              <Route path="/products/40-pack" element={<PackPage slug="40-pack" />} />
              <Route path="/products/50-pack" element={<PackPage slug="50-pack" />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/returns-warranty" element={<ReturnsWarranty />} />
              {/* Alias so /returns and /warranty also resolve to the policy page */}
              <Route path="/returns" element={<ReturnsWarranty />} />
              <Route path="/warranty" element={<ReturnsWarranty />} />
              <Route path="/trade" element={<Trade />} />
              <Route path="/faq" element={<Faq />} />
              {/* International shipping is quote-only — Australian checkout is unchanged */}
              <Route path="/international-shipping" element={<InternationalShipping />} />
              <Route path="/international-quote" element={<InternationalQuote />} />
              {/* Stockist landing — the trade page handles stockist enquiries */}
              <Route path="/stockists" element={<Navigate to="/trade?type=Stockist" replace />} />
              <Route path="/review" element={<Review />} />
              {/* Alias — the review hub answers to both /review and /reviews */}
              <Route path="/reviews" element={<Review />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </div>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
